import React, { useState, useEffect } from 'react';
import { styles, colors, radius } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Maliyet Analiz Sayfası
 * - Reçete seç -> maliyet kırılımı göster
 * - Food cost ratio hesapla
 * - Simülasyon: fiyat değişikliği etkisi
 * - Maliyet geçmişi
 * - Tüm maliyetleri toplu güncelle
 */
// marka_kod -> ad için minik fallback (yalnız /api/markalar erişilemezse; Anayasa M.1: kaynak DB).
const MARKA_AD_FALLBACK = {
  TORUNA_KASAP: 'Toruna Kasap',
  INGA_BURGER: 'Inga Burger',
  ALTI_USTU_KOFTE: 'Altı Üstü Köfte',
};

const MaliyetAnalizPage = () => {
  const [receteler, setReceteler] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [costResult, setCostResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gecmis, setGecmis] = useState([]);
  const [markalar, setMarkalar] = useState([]);

  // Simülasyon state
  const [simFiyatlar, setSimFiyatlar] = useState([]);
  const [simResult, setSimResult] = useState(null);

  // Toplu güncelle
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/recipes?limit=200`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => setReceteler(Array.isArray(data) ? data : []))
      .catch(() => []);

    // İşletme (marka) listesi — kod->ad eşlemesi için (DB'den, hardcode yasak — Anayasa M.1)
    fetch(`${API_URL}/api/markalar`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setMarkalar(Array.isArray(d) ? d : (d.markalar || [])))
      .catch(() => setMarkalar([]));
  }, []);

  const handleReceteSelect = async (id) => {
    setSelectedId(id);
    setCostResult(null);
    setSimResult(null);
    setSimFiyatlar([]);
    setGecmis([]);
    if (!id) return;

    setLoading(true);
    try {
      const [costRes, gecmisRes] = await Promise.all([
        fetch(`${API_URL}/api/recipes/${id}/cost`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API_URL}/api/recipes/${id}/cost-history`, { headers: getAuthHeaders() }).then(r => r.json()).catch(() => [])
      ]);
      setCostResult(costRes);
      setGecmis(Array.isArray(gecmisRes) ? gecmisRes : []);
    } catch (err) {
      logError('Maliyet hesaplanamadı:', err);
    }
    setLoading(false);
  };

  const handleBulkUpdate = async () => {
    if (!window.confirm('Tüm aktif reçetelerin maliyeti yeniden hesaplanacak ve kaydedilecek. Devam etmek istiyor musunuz?')) return;

    setBulkUpdating(true);
    setBulkResult(null);
    try {
      const res = await fetch(`${API_URL}/api/recipes/bulk-cost-save`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setBulkResult(data);

      // Seçili reçete varsa maliyetini yeniden yükle
      if (selectedId) {
        handleReceteSelect(selectedId);
      }
    } catch (err) {
      logError('Toplu güncelleme hatası:', err);
      setBulkResult({ hata: 1, message: 'Bağlantı hatası oluştu' });
    }
    setBulkUpdating(false);
  };

  const handleSimulasyon = async () => {
    if (!selectedId || simFiyatlar.length === 0) return;
    try {
      const res = await fetch(`${API_URL}/api/recipes/${selectedId}/cost/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          fiyat_degisiklikleri: simFiyatlar.filter(f => f.malzeme_id && f.yeni_fiyat)
        })
      });
      const data = await res.json();
      setSimResult(data);
    } catch (err) {
      logError('Simülasyon hatası:', err);
    }
  };

  const handleSnapshotKaydet = async () => {
    if (!selectedId) return;
    try {
      const res = await fetch(`${API_URL}/api/recipes/${selectedId}/cost/save`, { method: 'POST', headers: getAuthHeaders() });
      if (res.ok) {
        alert('Maliyet snapshot kaydedildi!');
        const gecmisRes = await fetch(`${API_URL}/api/recipes/${selectedId}/cost-history`, { headers: getAuthHeaders() }).then(r => r.json());
        setGecmis(Array.isArray(gecmisRes) ? gecmisRes : []);
      }
    } catch (err) {
      logError('Snapshot kaydetme hatası:', err);
    }
  };

  const addSimFiyat = () => {
    setSimFiyatlar(prev => [...prev, { malzeme_id: '', yeni_fiyat: '' }]);
  };

  const updateSimFiyat = (idx, field, value) => {
    setSimFiyatlar(prev => {
      const nf = [...prev];
      nf[idx] = { ...nf[idx], [field]: value };
      return nf;
    });
  };

  // Reçete listesi: sadece TABAK + POS satış fiyatı tanımlı olanlar
  const filteredReceteler = receteler.filter(r => r.tip === 'TABAK' && r.pos_satis_fiyati > 0);

  // Seçili reçetenin POS fiyatı (read-only badge için)
  const selectedRecipe = receteler.find(r => r.id === parseInt(selectedId));
  const posFiyat = selectedRecipe?.pos_satis_fiyati;

  // Food cost backend response'undan (3 metrik)
  const foodCostRatio = costResult?.food_cost_ratio;
  const yenilebilirFC = costResult?.yenilebilir_fc_ratio;
  const ambalajOran = costResult?.ambalaj_oran;

  const KaynakBadge = ({ kaynak }) => {
    const isParcalama = kaynak === 'PARCALAMA';
    // İç satış fiyatı kaynakları (Faz 1+2): marka iç satış fiyatı kullanıldı
    const isIcSatis = kaynak && kaynak.startsWith('IC_SATIS(');
    // İç satış kaydı yok → dış fiyat × marka marjı ile tahmin (uyarı)
    const isIcSatisFallback = kaynak && kaynak.startsWith('IC_SATIS_FALLBACK');

    const colors = {
      'MALZEME': { bg: '#e3f2fd', color: '#1565c0' },
      'RECETE': { bg: '#f3e5f5', color: '#6a1b9a' },
      'PARCALAMA': { bg: '#fff3e0', color: '#e65100' },
      'TRANSFER': { bg: '#e0f2f1', color: '#00695c' },
      'SIMULASYON': { bg: '#fce4ec', color: '#880e4f' },
      'TEDARIKCI': { bg: '#e8f5e9', color: '#2e7d32' }
    };

    let s, etiket, ipucu, kenarlik;
    if (isIcSatisFallback) {
      s = { bg: '#fff3e0', color: '#bf360c' };
      etiket = 'İÇ SATIŞ (TAHMİN)';
      ipucu = `${kaynak} — bu malzeme için iç satış kaydı yok; dış fiyat × marka marjı ile tahmin edildi`;
      kenarlik = '1px solid #bf360c';
    } else if (isIcSatis) {
      s = { bg: '#e0f7fa', color: '#006064' };
      etiket = 'İÇ SATIŞ';
      ipucu = `${kaynak} — marka iç satış fiyatı (Şirketler Arası Satış) kullanıldı`;
      kenarlik = '1px solid #006064';
    } else if (isParcalama) {
      s = colors['PARCALAMA'];
      etiket = kaynak;
      ipucu = kaynak;
      kenarlik = 'none';
    } else {
      s = colors[kaynak] || { bg: '#e9ecef', color: '#495057' };
      etiket = kaynak;
      ipucu = kaynak;
      kenarlik = 'none';
    }

    const yardimImleci = isIcSatis || isIcSatisFallback;

    return (
      <span
        title={ipucu}
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: radius.lg,
          fontSize: '10px',
          fontWeight: 'bold',
          background: s.bg,
          color: s.color,
          cursor: yardimImleci ? 'help' : 'default',
          border: kenarlik
        }}
      >
        {etiket}
      </span>
    );
  };

  // İşletme (marka) adı: kod -> ad. Önce /api/markalar (DB), erişilemezse minik fallback.
  const markaAdiCoz = (kod) => {
    if (!kod) return null;
    const m = markalar.find(x => x.kod === kod);
    if (m && m.ad) return m.ad;
    return MARKA_AD_FALLBACK[kod] || kod;
  };

  // Maliyet özetindeki işletme rozeti metni
  const markaKod = costResult?.marka_kod;
  const markaRozetMetni = markaKod == null
    ? 'Genel'
    : markaKod === 'TORUNA_KASAP'
      ? 'Toruna (dış fiyat)'
      : markaAdiCoz(markaKod);

  return (
    <div>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${styles.primary}, ${styles.primaryDark})`,
        color: 'white', padding: '15px 25px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <h1 style={{ margin: 0, fontSize: '20px' }}>Maliyet Analizi</h1>
        <button
          onClick={handleBulkUpdate}
          disabled={bulkUpdating}
          style={{
            padding: '8px 16px',
            background: bulkUpdating ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.4)',
            borderRadius: radius.sm,
            cursor: bulkUpdating ? 'wait' : 'pointer',
            fontSize: '13px',
            fontWeight: 600
          }}
        >
          {bulkUpdating ? 'Güncelleniyor...' : 'Tüm Maliyetleri Güncelle'}
        </button>
      </div>

      <div style={{ padding: '20px', maxWidth: '1400px' }}>
        {/* Toplu Güncelleme Sonucu */}
        {bulkResult && (
          <div style={{
            background: bulkResult.hata > 0 ? '#fff3e0' : '#e8f5e9',
            border: `1px solid ${bulkResult.hata > 0 ? '#ffcc02' : '#a5d6a7'}`,
            borderRadius: radius.md,
            padding: '12px 16px',
            marginBottom: '15px',
            fontSize: '13px'
          }}>
            <strong>{bulkResult.message}</strong>
            {bulkResult.guncellenen > 0 && (
              <span style={{ marginLeft: '10px', color: '#2e7d32' }}>
                ({bulkResult.guncellenen} reçete güncellendi)
              </span>
            )}
            {bulkResult.hata > 0 && (
              <span style={{ marginLeft: '10px', color: '#e65100' }}>
                ({bulkResult.hata} hata)
              </span>
            )}
            <button
              onClick={() => setBulkResult(null)}
              style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', color: colors.textTertiary, fontSize: '14px' }}
            >
              ✕
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Sol Panel: Reçete Seçimi & Kırılım */}
          <div>
            {/* Reçete Seçimi */}
            <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '15px', marginBottom: '15px' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>Reçete Seçimi</h3>
              <select value={selectedId} onChange={e => handleReceteSelect(e.target.value)}
                style={{ width: '100%', padding: '10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
                <option value="">-- Reçete Seçiniz --</option>
                {filteredReceteler.map(r => (
                  <option key={r.id} value={r.id}>{r.recete_kodu} - {r.ad} ({r.tip})</option>
                ))}
              </select>

              {/* POS Satış Fiyatı (read-only) & Food Cost */}
              {selectedId && (
                <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {posFiyat ? (
                    <div style={{ background: '#e3f2fd', padding: '8px 12px', borderRadius: radius.sm, fontSize: '12px', color: '#1565c0' }}>
                      POS Satış Fiyatı: <strong>₺{posFiyat.toFixed(2)}</strong>
                    </div>
                  ) : (
                    <div style={{ background: '#fff3cd', padding: '8px 12px', borderRadius: radius.sm, color: '#856404', fontSize: '12px' }}>
                      Bu reçete için POS satış fiyatı tanımlı değil. Modpos eşleştirmesi gerekir.
                    </div>
                  )}
                  {yenilebilirFC != null && (
                    <span title="Sadece yenilebilir hammadde / POS — F&B endüstri benchmark'ı (ambalaj hariç)" style={{
                      padding: '8px 14px', borderRadius: radius.sm, fontWeight: 'bold', fontSize: '14px',
                      background: yenilebilirFC < 28 ? '#d4edda' : yenilebilirFC <= 35 ? '#fff3cd' : '#f8d7da',
                      color: yenilebilirFC < 28 ? '#155724' : yenilebilirFC <= 35 ? '#856404' : '#721c24'
                    }}>
                      Food Cost (yenilebilir): %{yenilebilirFC.toFixed(1)}
                    </span>
                  )}
                  {ambalajOran != null && ambalajOran > 0 && (
                    <span title="Ambalaj kalemleri / POS — paket servis maliyeti payı" style={{
                      padding: '8px 14px', borderRadius: radius.sm, fontWeight: 'bold', fontSize: '14px',
                      background: '#e8eaf6', color: '#3f51b5'
                    }}>
                      Ambalaj: %{ambalajOran.toFixed(1)}
                    </span>
                  )}
                  {foodCostRatio != null && ambalajOran > 0 && (
                    <span title="(Yenilebilir + Ambalaj) / POS — paket dahil toplam maliyet payı" style={{
                      padding: '8px 14px', borderRadius: radius.sm, fontWeight: 'bold', fontSize: '14px',
                      background: foodCostRatio < 35 ? '#d4edda' : foodCostRatio <= 50 ? '#fff3cd' : '#f8d7da',
                      color: foodCostRatio < 35 ? '#155724' : foodCostRatio <= 50 ? '#856404' : '#721c24'
                    }}>
                      Toplam Cost: %{foodCostRatio.toFixed(1)}
                    </span>
                  )}
                  {/* Ambalajsız reçeteler için tek rozet (yenilebilir = toplam) — backward-compat */}
                  {foodCostRatio != null && yenilebilirFC == null && (
                    <span style={{
                      padding: '8px 14px', borderRadius: radius.sm, fontWeight: 'bold', fontSize: '14px',
                      background: foodCostRatio < 28 ? '#d4edda' : foodCostRatio <= 35 ? '#fff3cd' : '#f8d7da',
                      color: foodCostRatio < 28 ? '#155724' : foodCostRatio <= 35 ? '#856404' : '#721c24'
                    }}>
                      Food Cost: %{foodCostRatio.toFixed(1)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Maliyet Özeti */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Hesaplanıyor...</div>
            ) : costResult ? (
              <>
                {/* İşletme (marka) rozeti — maliyet hangi işletmenin iç fiyatıyla hesaplandı */}
                <div style={{ marginBottom: '12px' }}>
                  <span
                    title={markaKod
                      ? (markaKod === 'TORUNA_KASAP'
                          ? 'Toruna iç fiyat uygulanmaz; dış (tedarikçi) fiyatları kullanılır'
                          : 'Maliyet bu işletmenin iç satış fiyatlarıyla hesaplandı')
                      : 'İşletmesiz (Genel) reçete — iç satış marjı uygulanmaz'}
                    style={{
                      display: 'inline-block',
                      padding: '6px 14px',
                      borderRadius: radius.lg,
                      fontSize: '13px',
                      fontWeight: 'bold',
                      background: markaKod && markaKod !== 'TORUNA_KASAP' ? '#e0f7fa' : '#eef2f6',
                      color: markaKod && markaKod !== 'TORUNA_KASAP' ? '#006064' : colors.textSecondary,
                      border: `1px solid ${markaKod && markaKod !== 'TORUNA_KASAP' ? '#006064' : colors.border}`,
                      cursor: 'help'
                    }}
                  >
                    🏢 İşletme: {markaRozetMetni}
                  </span>
                </div>

                {/* İç satış fiyatı eksik uyarı banner'ı — tahmini fiyat kullanılan satırlar */}
                {costResult.ic_satis_eksik && (
                  <div style={{
                    background: '#fff3e0',
                    border: '1px solid #ffb74d',
                    borderRadius: radius.md,
                    padding: '12px 16px',
                    marginBottom: '15px',
                    fontSize: '13px',
                    color: '#bf360c',
                    lineHeight: 1.5
                  }}>
                    <strong>⚠️ Tahmini fiyat kullanıldı.</strong>{' '}
                    Şu malzemeler için iç satış kaydı yok, dış fiyat × marka marjı ile tahmin edildi:{' '}
                    <strong>
                      {(costResult.ic_satis_eksik_satirlar || [])
                        .map(l => l.malzeme)
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </strong>
                    {' '}— gerçek fiyat için Şirketler Arası Satış'tan satış girilmeli.
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '15px' }}>
                  <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1565c0' }}>₺{costResult.toplam_maliyet?.toFixed(2)}</div>
                    <div style={{ fontSize: '11px', color: '#1565c0' }}>Toplam Maliyet</div>
                  </div>
                  <div style={{ background: '#e8f5e9', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2e7d32' }}>
                      {costResult.porsiyon_maliyet ? `₺${costResult.porsiyon_maliyet.toFixed(2)}` : '-'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#2e7d32' }}>Birim Maliyet</div>
                  </div>
                  <div style={{ background: '#f3e5f5', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#6a1b9a' }}>{costResult.breakdown?.length || 0}</div>
                    <div style={{ fontSize: '11px', color: '#6a1b9a' }}>Kalem Sayısı</div>
                  </div>
                </div>

                {/* Kırılım Tablosu */}
                <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0, fontSize: '14px', color: '#333' }}>Maliyet Kırılımı</h3>
                    <button onClick={handleSnapshotKaydet}
                      style={{ background: '#e8f5e9', border: '1px solid #4caf50', borderRadius: radius.sm, padding: '4px 12px', cursor: 'pointer', fontSize: '11px', color: '#2e7d32' }}>
                      Snapshot Kaydet
                    </button>
                  </div>
                  {costResult.kilitli && (
                    <div style={{ padding: '20px', textAlign: 'center', color: colors.textSecondary, fontSize: '12px', background: colors.backgroundAlt, borderRadius: radius.sm }}>
                      🔒 Gizli tarif — malzeme kırılımı yetkilendirilmiş kişilere açıktır. Toplam maliyet yukarıda görünür.
                    </div>
                  )}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Malzeme</th>
                        <th style={{ padding: '8px', textAlign: 'right' }}>Miktar</th>
                        <th style={{ padding: '8px', textAlign: 'right' }}>Fire%</th>
                        <th style={{ padding: '8px', textAlign: 'right' }}>Brüt</th>
                        <th style={{ padding: '8px', textAlign: 'right' }}>₺/Birim</th>
                        <th style={{ padding: '8px', textAlign: 'center' }}>Kaynak</th>
                        <th style={{ padding: '8px', textAlign: 'right' }}>Maliyet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costResult.breakdown?.map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <td style={{ padding: '6px 8px' }}>
                            <div style={{ fontWeight: 'bold' }}>{item.malzeme_ad}</div>
                            <div style={{ fontSize: '10px', color: '#888', fontFamily: 'monospace' }}>{item.malzeme_kod}</div>
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{item.miktar} {item.birim}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: item.fire_orani > 0 ? '#e65100' : '#888' }}>
                            {item.fire_orani > 0 ? `${item.fire_orani}%` : '-'}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{item.brut_miktar.toFixed(3)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>₺{item.birim_fiyat.toFixed(2)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}><KaynakBadge kaynak={item.kaynak} /></td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>₺{item.satir_maliyet.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: colors.backgroundAlt, fontWeight: 'bold' }}>
                        <td colSpan={6} style={{ padding: '8px', textAlign: 'right' }}>TOPLAM:</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: styles.primary }}>
                          ₺{costResult.toplam_maliyet?.toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            ) : selectedId ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Reçete seçin...</div>
            ) : null}
          </div>

          {/* Sağ Panel: Simülasyon & Geçmiş */}
          <div>
            {/* Simülasyon */}
            <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '15px', marginBottom: '15px' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>Fiyat Simülasyonu</h3>
              <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '0 0 10px 0' }}>
                "Ya X malzemenin fiyatı Y olursa?" - maliyet etkisini hesaplayın
              </p>

              {simFiyatlar.map((f, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <select value={f.malzeme_id} onChange={e => updateSimFiyat(idx, 'malzeme_id', parseInt(e.target.value))}
                    style={{ flex: 2, padding: '6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px' }}>
                    <option value="">-- Malzeme --</option>
                    {costResult?.breakdown?.map(b => (
                      <option key={b.malzeme_kod} value={b.malzeme_kod}>
                        {b.malzeme_ad}
                      </option>
                    ))}
                  </select>
                  <input type="number" placeholder="Yeni ₺" value={f.yeni_fiyat}
                    onChange={e => updateSimFiyat(idx, 'yeni_fiyat', parseFloat(e.target.value))}
                    style={{ flex: 1, padding: '6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px' }} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={addSimFiyat}
                  style={{ background: '#e3f2fd', border: '1px solid #42a5f5', borderRadius: radius.sm, padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>
                  + Fiyat Ekle
                </button>
                {simFiyatlar.length > 0 && (
                  <button onClick={handleSimulasyon}
                    style={{ background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>
                    Hesapla
                  </button>
                )}
              </div>

              {simResult && (
                <div style={{ marginTop: '15px', padding: '12px', background: '#fff3e0', borderRadius: radius.sm }}>
                  <strong>Simülasyon Sonucu:</strong>
                  <div style={{ marginTop: '5px' }}>
                    Toplam: ₺{simResult.toplam_maliyet?.toFixed(2)}
                    {costResult && (
                      <span style={{ marginLeft: '10px', color: simResult.toplam_maliyet > costResult.toplam_maliyet ? '#c62828' : '#2e7d32' }}>
                        ({simResult.toplam_maliyet > costResult.toplam_maliyet ? '↑' : '↓'}
                        ₺{Math.abs(simResult.toplam_maliyet - costResult.toplam_maliyet).toFixed(2)})
                      </span>
                    )}
                  </div>
                  {simResult.porsiyon_maliyet && (
                    <div>Birim: ₺{simResult.porsiyon_maliyet?.toFixed(2)}</div>
                  )}
                </div>
              )}
            </div>

            {/* Maliyet Geçmişi */}
            <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '15px' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>Maliyet Geçmişi</h3>
              {gecmis.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#888', fontSize: '12px' }}>
                  {selectedId ? 'Henüz maliyet geçmişi yok. Snapshot kaydedin.' : 'Reçete seçiniz.'}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt, borderBottom: `1px solid ${colors.border}` }}>
                      <th style={{ padding: '6px', textAlign: 'left' }}>Tarih</th>
                      <th style={{ padding: '6px', textAlign: 'right' }}>Toplam</th>
                      <th style={{ padding: '6px', textAlign: 'right' }}>Birim</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gecmis.map((g, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={{ padding: '6px' }}>{new Date(g.hesaplama_tarihi).toLocaleDateString('tr-TR')}</td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>₺{g.toplam_maliyet?.toFixed(2)}</td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>₺{g.porsiyon_maliyet?.toFixed(2) || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaliyetAnalizPage;
